import { baseApi } from '../../../core/baseApi';
import { createCreateMutation, createUpdateMutation, createDeleteMutation, unwrapApiResponse, unwrapListResponse, buildUrl, generateListProvideTags, GetListArgs, ApiResponse } from '../../../patterns/crud';
import { extractErrorMessage } from '@/shared/utils/toast';
import { apiLogger } from '@/core/utils/logger';
import { transformTeamApiMessage } from '../transformers/message';
import type { Message, TeamChatConversation } from '../types';
import type { ApiMessageLike } from '../transformers/message';
import type { Schema } from '@voley/shared-types';

// Simple interface for team entities that have IDs
interface TeamEntity {
  id: string;
  [key: string]: unknown;
}

// Membership data shape returned by read-receipt queries
interface MembershipData {
  membership?: {
    showReadReceipts?: boolean;
  };
}

// Body shape for sending a team chat message
interface SendMessageBody {
  content?: string;
  messageType?: string;
  parentMessageId?: string;
  [key: string]: unknown;
}

export const teamChatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Conversations
    getTeamConversations: builder.query<TeamEntity[], GetListArgs | void>({
      query: (params) => buildUrl('/team-chat/conversations', params as GetListArgs | undefined),
      transformResponse: unwrapListResponse<TeamEntity>,
      providesTags: (result) => generateListProvideTags('TeamConversations', result, 'LIST'),
      // High-volatility endpoint: refetch if data is older than 5 minutes on mount
      // Team conversation list should stay fresh for sidebar navigation
      // @ts-expect-error - RTK Query supports this at endpoint level but types don't include it
      refetchOnMountOrArgChange: 5 * 60,
    }),
    getTeamConversation: builder.query<TeamChatConversation, string>({
      query: (id: string) => `/team-chat/conversations/${id}`,
      transformResponse: (response: ApiResponse<Record<string, unknown>>) => {
        const unwrapped = unwrapApiResponse(response);

        // Extract just the conversation object since messages are fetched separately
        if (unwrapped && typeof unwrapped === 'object' && 'conversation' in unwrapped) {
          return (unwrapped as { conversation: TeamChatConversation }).conversation;
        }

        // API boundary: runtime data is structurally TeamChatConversation
        return unwrapped as unknown as TeamChatConversation;
      },
      providesTags: (_result, _error, id: string) => [{ type: 'TeamConversations', id }],
      // Keep cached data for 5 minutes so conversation metadata survives typical navigation
      keepUnusedDataFor: 300
    }),
    createGroupConversation: builder.mutation<unknown, { data: Pick<Schema<'CreateUserGroupBody'>, 'name'> & Partial<Schema<'CreateUserGroupBody'>> }>({
      query: ({ data }) => ({
        url: '/user-groups',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: 'TeamConversations', id: 'LIST' },
        { type: 'TeamMembers', id: 'LIST' }
      ]
    }),
    startDirectConversation: createCreateMutation<TeamEntity>('/team-chat/conversations/direct', 'TeamConversations')(builder),
    
    // Messages
    // Fetch conversation messages from the unified messages controller
    getTeamMessages: builder.query<Message[], { parentId: string; page?: number; limit?: number }>({
      query: ({ parentId, ...params }) => ({
        url: `/messages/conversation/${parentId}`,
        params
      }),
      transformResponse: (response: unknown) => {
        // The messages controller returns { success, data: { messages, total } }.
        // unwrapListResponse expects data to be a flat array, so we handle
        // the nested { messages } envelope explicitly — same pattern as the
        // SMS messaging endpoint (messaging/endpoints/messages.ts).
        const res = response as Record<string, unknown>;
        let raw: unknown[] = [];

        const data = res?.data as Record<string, unknown> | unknown[] | undefined;
        if (data && !Array.isArray(data) && Array.isArray((data as Record<string, unknown>).messages)) {
          raw = (data as Record<string, unknown>).messages as unknown[];
        } else if (Array.isArray(data)) {
          raw = data;
        } else if (Array.isArray(res?.messages)) {
          raw = res.messages as unknown[];
        } else if (Array.isArray(response)) {
          raw = response;
        }

        return raw.map((m) => transformTeamApiMessage(m as ApiMessageLike));
      },
      providesTags: (result, _error, { parentId }) => {
        const base = [
          { type: 'Messages' as const, id: `${parentId}:list` },
          { type: 'TeamConversations' as const, id: parentId }
        ];
        if (!result) return base;
        return [
          ...base,
          ...result.map((m) => ({ type: 'Messages' as const, id: m.id }))
        ];
      },
      // Keep messages cached for 5 minutes to match conversation cache (survives navigation)
      keepUnusedDataFor: 300
    }),
    // Ensure this posts to /team-chat/conversations/:id/messages which backend expects
    sendTeamMessage: builder.mutation<TeamEntity, { parentId: string; data: SendMessageBody; tempId?: string }>({
      query: ({ parentId, data }) => ({
        url: `/team-chat/conversations/${parentId}/messages`,
        method: 'POST',
        body: data
      }),
      // NOTE: No optimistic insert here — MessageHandling.tsx already inserts the
      // optimistic message before triggering the HTTP path. This handler only
      // reconciles the optimistic entry with the real server response.
      async onQueryStarted({ parentId, tempId }, { dispatch, queryFulfilled }) {
        const optimisticTempId = tempId || '';

        try {
          const { data: result } = await queryFulfilled;
          const realMessage = transformTeamApiMessage(result as ApiMessageLike);

          // Update the optimistic message in-place with server data.
          // Preserves array position and key stability (no splice+push).
          dispatch(
            teamChatApi.util.updateQueryData('getTeamMessages', { parentId, page: 0, limit: 50 }, (draft) => {
              const optimisticIndex = optimisticTempId
                ? draft.findIndex((m) => m.id === optimisticTempId || m.tempId === optimisticTempId)
                : -1;

              if (optimisticIndex !== -1) {
                // In-place update: merge server data while preserving tempId for dedup
                draft[optimisticIndex] = {
                  ...draft[optimisticIndex],
                  ...realMessage,
                  tempId: optimisticTempId,
                  status: 'sent',
                };
              } else {
                // Socket may have already inserted the real message
                const existingRealIndex = draft.findIndex((m) => m.id === realMessage.id);
                if (existingRealIndex === -1) {
                  draft.push({ ...realMessage, status: 'sent' });
                } else {
                  draft[existingRealIndex] = { ...draft[existingRealIndex], ...realMessage, status: 'sent' };
                }
              }
            })
          );
        } catch (error: unknown) {
          // On error, mark message as failed (don't remove it)
          apiLogger.error('[sendTeamMessage] Error:', error);

          if (optimisticTempId) {
            dispatch(
              teamChatApi.util.updateQueryData('getTeamMessages', { parentId, page: 0, limit: 50 }, (draft) => {
                const optimisticIndex = draft.findIndex((m) =>
                  m.id === optimisticTempId || m.tempId === optimisticTempId
                );
                if (optimisticIndex !== -1) {
                  draft[optimisticIndex] = {
                    ...draft[optimisticIndex],
                    status: 'failed' as const,
                    metadata: {
                      ...draft[optimisticIndex].metadata,
                      error: extractErrorMessage(error, 'Failed to send message')
                    }
                  };
                }
              })
            );
          }
        }
      },
      // Don't invalidate tags - optimistic updates handle the cache
      invalidatesTags: []
    }),
    
    // Team members - REMOVED: Use organization members API instead
    
    // Actions
    markTeamConversationRead: createUpdateMutation<TeamEntity>('/team-chat/conversations', 'TeamConversations')(builder),
    toggleTeamConversationPin: builder.mutation<unknown, { id: string; data: { isPinned: boolean } }>({
      query: ({ id, data }) => ({
        url: `/team-chat/conversations/${id}/toggle-pin`,
        method: 'POST',
        body: data
      }),

      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        // Optimistically update the conversation details cache
        const patchConversation = dispatch(
          teamChatApi.util.updateQueryData('getTeamConversation', id, (draft) => {
            if (draft) {
              draft.isPinned = data.isPinned;
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic update on failure
          patchConversation.undo();
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TeamConversations', id },
        { type: 'TeamConversations', id: 'LIST' }
      ]
    }),
    updateChannelName: builder.mutation<unknown, { id: string; data: { name: string } }>({
      query: ({ id, data }) => ({
        url: `/user-groups/${id}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'TeamConversations', id },
        { type: 'TeamConversations', id: 'LIST' }
      ]
    }),
    updateChannelAvatar: createUpdateMutation<TeamEntity>('/user-groups', 'TeamConversations')(builder),
    
    // Upload channel avatar via backend (backend uploads to Cloudinary)
    uploadChannelAvatar: builder.mutation<
      { avatarUrl: string; thumbnailUrl?: string; mediumUrl?: string; group: unknown },
      { id: string; file: File; conversationId?: string }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/user-groups/${id}/avatar`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: ApiResponse<Record<string, unknown>>) =>
        unwrapApiResponse(response) as { avatarUrl: string; thumbnailUrl?: string; mediumUrl?: string; group: unknown },
      invalidatesTags: (_result, _error, { id, conversationId }) => [
        { type: 'TeamConversations', id },
        ...(conversationId ? [{ type: 'TeamConversations' as const, id: conversationId }] : []),
        { type: 'TeamConversations', id: 'LIST' }
      ]
    }),

    // Remove channel avatar
    removeChannelAvatar: builder.mutation<{ group: unknown }, { id: string; conversationId?: string }>({
      query: ({ id }) => ({
        url: `/user-groups/${id}/avatar`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id, conversationId }) => [
        { type: 'TeamConversations', id },
        ...(conversationId ? [{ type: 'TeamConversations' as const, id: conversationId }] : []),
        { type: 'TeamConversations', id: 'LIST' }
      ]
    }),
    
    updateChannelDescription: createUpdateMutation<TeamEntity>('/user-groups', 'TeamConversations')(builder),
    
    // Message reactions - use optimistic updates since backend doesn't return reactions in message list
    addMessageReaction: builder.mutation<unknown, { messageId: string; emoji: string; conversationId: string; userId: string }>({
      query: ({ messageId, emoji }) => ({
        url: `/team-chat/messages/${messageId}/reactions`,
        method: 'POST',
        body: { emoji }
      }),
      // Use optimistic update instead of invalidating all Messages
      async onQueryStarted({ messageId, emoji, conversationId, userId }, { dispatch, queryFulfilled }) {
        // Optimistically update the cache
        const patchResult = dispatch(
          teamChatApi.util.updateQueryData('getTeamMessages', { parentId: conversationId, page: 0, limit: 50 }, (draft) => {
            const message = draft.find((m) => m.id === messageId);
            if (message) {
              if (!message.reactions) message.reactions = {};
              if (!message.reactions[emoji]) message.reactions[emoji] = [];
              if (!message.reactions[emoji].includes(userId)) {
                message.reactions[emoji].push(userId);
              }
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResult.undo();
        }
      }
    }),
    removeMessageReaction: builder.mutation<unknown, { messageId: string; emoji: string; conversationId: string; userId: string }>({
      query: ({ messageId, emoji }) => ({
        url: `/team-chat/messages/${messageId}/reactions`,
        method: 'DELETE',
        body: { emoji }
      }),
      // Use optimistic update instead of invalidating all Messages
      async onQueryStarted({ messageId, emoji, conversationId, userId }, { dispatch, queryFulfilled }) {
        // Optimistically update the cache
        const patchResult = dispatch(
          teamChatApi.util.updateQueryData('getTeamMessages', { parentId: conversationId, page: 0, limit: 50 }, (draft) => {
            const message = draft.find((m) => m.id === messageId);
            if (message && message.reactions && message.reactions[emoji]) {
              message.reactions[emoji] = message.reactions[emoji].filter((id: string) => id !== userId);
              // Remove the emoji key if no users left
              if (message.reactions[emoji].length === 0) {
                delete message.reactions[emoji];
              }
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResult.undo();
        }
      }
    }),
    
    // Channel/Group Management
    addMemberToChannel: builder.mutation<unknown, { channelId: string; userId: string; role?: string }>({
      query: ({ channelId, userId, role }) => ({
        url: `/user-groups/${channelId}/members`,
        method: 'POST',
        body: { userId, role }
      }),
      invalidatesTags: [
        { type: 'TeamConversations', id: 'LIST' },
        { type: 'TeamMembers', id: 'LIST' }
      ]
    }),
    deleteChannel: createDeleteMutation<unknown>('/user-groups', 'TeamConversations')(builder),
    leaveConversation: builder.mutation<unknown, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/team-chat/conversations/${conversationId}/leave`,
        method: 'POST',
        body: {}
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: 'TeamConversations', id: conversationId },
        { type: 'TeamConversations', id: 'LIST' }
      ]
    }),
    removeMemberFromChannel: builder.mutation<unknown, { channelId: string; memberId: string }>({
      query: ({ channelId, memberId }) => ({
        url: `/user-groups/${channelId}/members/${memberId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [
        { type: 'TeamConversations', id: 'LIST' },
        { type: 'TeamMembers', id: 'LIST' }
      ]
    }),

    // Read receipts settings
    updateReadReceiptSettings: builder.mutation<unknown, { groupId: string; showReadReceipts: boolean }>({
      query: ({ groupId, showReadReceipts }) => ({
        url: `/user-groups/${groupId}/read-receipts`,
        method: 'PUT',
        body: { showReadReceipts }
      }),
      async onQueryStarted({ groupId, showReadReceipts }, { dispatch, queryFulfilled }) {
        // Optimistically update the membership cache
        const patchResult = dispatch(
          teamChatApi.util.updateQueryData('getReadReceiptPreference', groupId, (draft) => {
            const d = draft as MembershipData;
            if (d?.membership) {
              d.membership.showReadReceipts = showReadReceipts;
            }
          })
        );

        try {
          const response = await queryFulfilled;
          // Update cache with server response to ensure consistency
          const serverData = response.data as MembershipData | undefined;
          if (serverData && 'membership' in serverData) {
            dispatch(
              teamChatApi.util.updateQueryData('getReadReceiptPreference', groupId, () => {
                return serverData;
              })
            );
          }
        } catch {
          // Revert optimistic update on failure
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        'TeamConversations',
        { type: 'TeamMembers', id: arg.groupId },
        'TeamMembers'
      ]
    }),
    getReadReceiptPreference: builder.query<unknown, string>({
      query: (groupId) => `/user-groups/${groupId}/membership`,
      transformResponse: (response: ApiResponse<unknown>) => {
        return unwrapApiResponse(response);
      },
      providesTags: (_result, _error, groupId) => [
        { type: 'TeamMembers', id: groupId },
        'TeamMembers'
      ],
      // Ensure fresh data on mount - data becomes stale after 30 seconds
      // Note: Use refetchOnMountOrArgChange at hook level if immediate refetch is needed
      keepUnusedDataFor: 30
    })

    // WebSocket subscription disabled - Socket.IO already handles team chat via join_team_group event
    // If needed in the future, implement: subscribeToTeamChat: createWebSocketSubscription('/team-chat/subscribe', 'TeamConversations')(builder),
  })
});

export const {
  useGetTeamConversationsQuery,
  useGetTeamConversationQuery,
  useCreateGroupConversationMutation,
  useStartDirectConversationMutation,
  useGetTeamMessagesQuery,
  useSendTeamMessageMutation,
  useMarkTeamConversationReadMutation,
  useToggleTeamConversationPinMutation,
  useUpdateChannelNameMutation,
  useUpdateChannelAvatarMutation,
  useUploadChannelAvatarMutation,
  useRemoveChannelAvatarMutation,
  useUpdateChannelDescriptionMutation,
  useAddMessageReactionMutation,
  useRemoveMessageReactionMutation,
  useAddMemberToChannelMutation,
  useDeleteChannelMutation,
  useLeaveConversationMutation,
  useRemoveMemberFromChannelMutation,
  useUpdateReadReceiptSettingsMutation,
  useGetReadReceiptPreferenceQuery,
  // useSubscribeToTeamChatQuery, // Disabled - use Socket.IO join_team_group event instead
} = teamChatApi;